import { Node, mergeAttributes } from '@tiptap/core';

/**
 * Markup the editor cannot author, kept exactly as it arrived.
 *
 * A schema-based editor only knows the shapes it models: hand a plain TipTap
 * document a table and the table is gone, silently, the next time anything is
 * saved. TinyMCE never did that — it edits HTML directly, so whatever it did
 * not understand simply stayed. Losing that on the way across would mean a
 * seller fixing a typo could delete the specification table from a page that
 * is already live on the storefront.
 *
 * So the constructs an imported listing actually carries — tables, images,
 * embedded video, figures — parse into one opaque block that stores its own
 * original HTML and renders it back byte-for-byte. It is an atom: the seller
 * can see it, select it and delete it, but cannot edit inside it, which is an
 * honest account of what this editor can do with it.
 */
export const PreservedHtml = Node.create({
  name: 'preservedHtml',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      html: {
        default: '',
        parseHTML: (element) => element.outerHTML,
        // Stored on the node, not emitted as an attribute — the HTML *is* the
        // rendered output, so writing it out again would nest it inside itself.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      // `figure` before `img` so a captioned image is kept whole rather than
      // stripped down to the picture inside it.
      { tag: 'figure', priority: 60 },
      { tag: 'table', priority: 60 },
      { tag: 'iframe', priority: 60 },
      { tag: 'video', priority: 60 },
      { tag: 'img', priority: 60 },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const html = String(node.attrs.html ?? '');
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const element = holder.firstElementChild;
    // ProseMirror accepts a DOM node as an output spec, which is what lets the
    // original markup come back out unchanged instead of being rebuilt from a
    // description of itself.
    if (element) return element;
    return ['div', mergeAttributes(HTMLAttributes)];
  },
});
